import {
  AttendanceEmployeesSettingExtraHoursBaselineEnum,
  AttendanceEmployeesSettingForgotClockOutOptionEnum,
  TimesheetPageEmployeesSettingFragment,
} from 'generated/resources/graphql'
import { first, orderBy } from 'lodash'
import moment from 'moment'
import { DeepPartial } from 'utility-types'

import { stringToDate } from 'modules/core/lib/dates'

export type EmployeesSettingEffectiveAtWithPolicyId = Pick<
  TimesheetPageEmployeesSettingFragment,
  'effectiveAt'
> & {
  attendanceEmployeesPolicy: Pick<
    TimesheetPageEmployeesSettingFragment['attendanceEmployeesPolicy'],
    'id'
  >
} & DeepPartial<TimesheetPageEmployeesSettingFragment>

class EmployeesSettingsUtils {
  static defaultEmployeesSetting<
    T extends DeepPartial<TimesheetPageEmployeesSettingFragment>
  >(): Pick<T, keyof TimesheetPageEmployeesSettingFragment> {
    return {
      id: 0,
      attendanceEmployeesPolicy: {
        id: 0,
      },
      autofillTimesheet: false,
      desktopClockin: false,
      editTimesheetAuthorization: false,
      enableBreaks: false,
      enableClockInLocation: false,
      faceRecognition: false,
      forgotClockOutToleranceMinutes: 0,
      geofencing: false,
      geolocation: false,
      idClockSharedDevice: false,
      mobileClockin: false,
      overtimeAuthorization: false,
      payrollCompensation: false,
      qrCodeClockin: false,
      timeoffCompensation: false,
    }
  }

  static hasGeofencing(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'geofencing'>[]
  ): boolean {
    return employeesSettings.some((employeesSetting) => employeesSetting.geofencing)
  }

  static hasGeoLocation(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'geolocation'>[]
  ): boolean {
    return employeesSettings.some((employeesSetting) => employeesSetting.geolocation)
  }

  static hasShiftLocation(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'enableClockInLocation'>[]
  ): boolean {
    return employeesSettings.some((employeesSetting) => employeesSetting.enableClockInLocation)
  }

  static hasOvertimeAuthorization(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'overtimeAuthorization'>[]
  ) {
    return employeesSettings.some((employeesSetting) => employeesSetting.overtimeAuthorization)
  }

  static hasEditTimesheetAuthorization(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'editTimesheetAuthorization'>[]
  ) {
    return employeesSettings.some((employeesSetting) => employeesSetting.editTimesheetAuthorization)
  }

  static hasLocationEnabled(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'enableClockInLocation'>[]
  ) {
    return employeesSettings.some((employeesSetting) => employeesSetting.enableClockInLocation)
  }

  static hasPayrollCompensationEnabled(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'payrollCompensation'>[]
  ) {
    return employeesSettings.some((employeesSetting) => employeesSetting.payrollCompensation)
  }

  static hasBalancePayrollCompensationEnabled(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'balancePayrollCompensation'>[]
  ) {
    return employeesSettings.some((employeesSetting) => employeesSetting.balancePayrollCompensation)
  }

  static hasTimeoffCompensationEnabled(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'timeoffCompensation'>[]
  ) {
    return employeesSettings.some((employeesSetting) => employeesSetting.timeoffCompensation)
  }

  static effectiveAt = (
    employeesSetting: Pick<TimesheetPageEmployeesSettingFragment, 'effectiveAt'>
  ) => {
    return employeesSetting.effectiveAt
      ? stringToDate(employeesSetting.effectiveAt, 'backend')
      : moment()
  }

  static isBeforeThanDate(
    employeesSetting: Pick<TimesheetPageEmployeesSettingFragment, 'effectiveAt'>,
    date: moment.Moment
  ) {
    return this.effectiveAt(employeesSetting).isSameOrBefore(date, 'day')
  }

  static isAfterThanNext(
    employeesSettings: EmployeesSettingEffectiveAtWithPolicyId[],
    index: number,
    date: moment.Moment
  ) {
    const employeesSetting = employeesSettings?.[index + 1]
    const effectiveAt = employeesSetting ? this.effectiveAt(employeesSetting) : null

    return (
      // last one
      effectiveAt?.isAfter(date, 'day') || index === employeesSettings.length - 1
    )
  }

  static currentSettingWithinDate = <T extends EmployeesSettingEffectiveAtWithPolicyId>(
    employeesSettings: T[],
    date: moment.Moment
  ) => {
    const currentSetting = employeesSettings.find(
      (employeesSetting, index: number) =>
        this.isBeforeThanDate(employeesSetting, date) &&
        this.isAfterThanNext(employeesSettings, index, date)
    )

    return currentSetting || first(employeesSettings)
  }

  static getSettingWithinDate = <T extends EmployeesSettingEffectiveAtWithPolicyId>(
    employeesSettings: T[],
    date: moment.Moment,
    attendancePolicyId?: number | null
  ) => {
    return (
      this.currentSettingWithinDate(
        this.orderByEffectiveAt<T>(employeesSettings, attendancePolicyId),
        date
      ) ?? this.defaultEmployeesSetting<T>()
    )
  }

  static byPolicy = <T extends EmployeesSettingEffectiveAtWithPolicyId>(
    employeesSettings: T[],
    attendancePolicyId: number
  ): T[] => {
    return employeesSettings.filter(
      (employeesSetting) => employeesSetting.attendanceEmployeesPolicy.id === attendancePolicyId
    )
  }

  static orderByEffectiveAt = <T extends EmployeesSettingEffectiveAtWithPolicyId>(
    employeesSettings: T[],
    attendancePolicyId?: number | null
  ): T[] => {
    if (attendancePolicyId) {
      return orderBy(
        this.byPolicy<T>(employeesSettings, attendancePolicyId),
        (setting: T) => setting.effectiveAt,
        'asc'
      )
    } else {
      return orderBy(employeesSettings, (setting: T) => setting.effectiveAt, 'asc')
    }
  }

  static hasAutofillTimesheet(
    employeesSettings: Array<
      Pick<TimesheetPageEmployeesSettingFragment, 'autofillTimesheet'> & {
        attendanceEmployeesPolicy: Pick<
          TimesheetPageEmployeesSettingFragment['attendanceEmployeesPolicy'],
          'id'
        >
      }
    >,
    policyId: number | null | undefined
  ): boolean {
    return employeesSettings.some(
      (employeesSetting) =>
        employeesSetting.autofillTimesheet &&
        employeesSetting.attendanceEmployeesPolicy.id == policyId
    )
  }

  static missingClockOutEnabled(
    employeesSetting?: Pick<TimesheetPageEmployeesSettingFragment, 'forgotClockOut'>
  ): boolean {
    const clockOutOption: AttendanceEmployeesSettingForgotClockOutOptionEnum = 'missing_clock_out'

    return employeesSetting?.forgotClockOut === clockOutOption
  }

  static clockOutReminderEnabled(
    employeesSetting?: Pick<TimesheetPageEmployeesSettingFragment, 'forgotClockOut'>
  ): boolean {
    const clockOutOption: AttendanceEmployeesSettingForgotClockOutOptionEnum = 'clock_out_reminder'

    return employeesSetting?.forgotClockOut === clockOutOption
  }

  static hasContractHoursBaseline(
    employeesSettings: Pick<TimesheetPageEmployeesSettingFragment, 'extraHoursBaseline'>[]
  ): boolean {
    const contractHoursOption: AttendanceEmployeesSettingExtraHoursBaselineEnum = 'contract_hours'

    return employeesSettings.some((setting) => setting?.extraHoursBaseline === contractHoursOption)
  }
}

export default EmployeesSettingsUtils
